import React, { useState, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import {
  Grid,
  Button,
  TextField,
  CircularProgress,
  FormControlLabel,
  Checkbox
} from '@mui/material';

// Dumb Components
import JsonViewer from './components/JsonViewer';
import DevStandardDropdown from './components/DevStandardDropdown';
import ElevationGraph from './components/ElevationGraph';

// Actions
import {
  createRoute,
  updateRoute,
  setNewGeoJson,
  setNewRouteStartPoint,
  setNewRoute,
  setUpdateGeoJson,
  toggleNewLineAndPoints,
  closeDeleteConfirm,
  deleteRoute,
  clearNewRoute,
  clearUpdateRoute,
  setTab,
  setRouteEircode,
  setRouteName,
  setRouteRelAlt,
  setRouteType,
  setHighway,
  setDisableSlopedAscent
} from 'actions/routePlannerActions';

// Hooks
import { useElevationDataForRoute } from 'hooks/routePlannerHooks';

const NewRoute = props => {
  const {
    creatingRoute,
    newRouteGeoJson,
    newRoute,
    newShowLine,
    toggleNewLineAndPoints,
    setNewGeoJson,
    setNewRoute,
    setNewRouteStartPoint,
    createRoute,
    newRouteStartPointElevation,
    routes,
    setName,
    name,
    routeType,
    setType,
    disableSlopedAscent
  } = props;

  const [elevationGraphDimensions, setElevationGraphDimensions] = useState();
  const dispatch = useDispatch();

  const [graphLines] = useElevationDataForRoute(
    newRoute?.misc1,
    newRouteStartPointElevation
  );

  useEffect(() => {
    const width = document.getElementById('json-editor').clientWidth * 0.95;
    const height = window.innerHeight * 0.3;
    setElevationGraphDimensions({ width, height });
  }, []);

  const onCreateRoute = () => {
    createRoute(
      newRouteGeoJson,
      name,
      routeType,
      disableSlopedAscent
    );
  };

  const onChangeJson = json => {
    setNewGeoJson(json);
  };

  const toggleLineAndPoints = () => {
    toggleNewLineAndPoints(!newShowLine);
  };

  const onChangeName = e => {
    setName(e.target.value);
  };

  const renderBtnText = () => {
    if (newRoute) {
      return creatingRoute ? 'Updating' : 'Update';
    } else {
      return creatingRoute ? 'Creating' : 'Create';
    }
  };

  const onChangeRouteType = e => {
    setType(e.target.value);
  };

  const onCheckDisableSlopedAscent = e => {
    dispatch(setDisableSlopedAscent(e.target.checked));
  };

  return (
    <div className="relative h-full" id="json-editor">
      <Grid
        className="p-1"
        spacing={1}
        container
        justify="space-between">
        <Grid item xs={4}>
          <TextField
            id="outlined-basic"
            margin="dense"
            fullWidth
            value={name}
            label="Name"
            variant="outlined"
            onChange={onChangeName}
          />
        </Grid>
        <Grid item xs={3}>
          <Button
            className="mt-2.5"
            disabled={!name || creatingRoute || !newShowLine}
            startIcon={creatingRoute ? <CircularProgress size={12} /> : null}
            variant="contained"
            color="primary"
            onClick={onCreateRoute}
            fullWidth>
            {renderBtnText()}
          </Button>
        </Grid>
      </Grid>
      <Grid container className="p-2" spacing={1}>
        <Grid item xs={4}>
          <FormControlLabel
            control={<Checkbox color="primary" />}
            checked={disableSlopedAscent}
            onChange={onCheckDisableSlopedAscent}
            label={'Disable Sloped Route'}
            labelPlacement="end"
          />
        </Grid>
        <Grid item xs={4}>
          <DevStandardDropdown value={routeType} onChange={onChangeRouteType} />
        </Grid>
      </Grid>
      <div
        className={graphLines ? "relative h-45vh overflow-y-scroll" : "relative h-full overflow-y-scroll"}>
        {newRoute && (
          <Button
            size="small"
            variant="contained"
            className="absolute top-5 right-6 z-50"
            onClick={toggleLineAndPoints}>
            {newShowLine ? 'Show Points' : 'Show LineString'}
          </Button>
        )}
        <JsonViewer value={newRouteGeoJson} onChangeJson={onChangeJson} />
      </div>
      {graphLines && elevationGraphDimensions && (
        <div className="p-5 relative">
          <ElevationGraph
            dronePlannedAltitude={graphLines.droneGraphLine}
            groundElevation={graphLines.groundGraphLine}
            height={elevationGraphDimensions.height}
            width={elevationGraphDimensions.width}
          />
        </div>
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  eircode: state.routePlanner.routeEircode,
  name: state.routePlanner.routeName,
  relAlt: state.routePlanner.relAlt,
  disableSlopedAscent: state.routePlanner.disableSlopedAscent,
  type: state.routePlanner.type,
  highway: state.routePlanner.highwayKey,
  routes: state.routePlanner.routes,
  highwayKeys: state.routePlanner.highwayKeys,
  creatingRoute: state.routePlanner.creatingRoute,
  newRouteGeoJson: state.routePlanner.newRouteGeoJson,
  newRoute: state.routePlanner.newRoute,
  newRouteStartPointElevation: state.routePlanner.newRouteStartPointElevation,
  newShowLine: state.routePlanner.newShowLine,
  tab: state.routePlanner.tab,
  routeType: state.routePlanner.routeType,
  newRouteStartPoint: state.routePlanner.newRouteStartPoint
});

const mapDispatchToProps = dispatch => ({
  createRoute: (
    eircode,
    lineString,
    name,
    routeModeDev,
    autoRelativeAlt,
    disableSlopedAscent
  ) =>
    dispatch(
      createRoute(
        eircode,
        lineString,
        name,
        routeModeDev,
        autoRelativeAlt,
        disableSlopedAscent
      )
    ),
  sendUpdatedRoute: (eircode, lineString) =>
    dispatch(updateRoute(eircode, lineString)),
  setNewGeoJson: gJson => dispatch(setNewGeoJson(gJson)),
  setNewRoute: route => dispatch(setNewRoute(route)),
  setNewRouteStartPoint: startPoint =>
    dispatch(setNewRouteStartPoint(startPoint)),
  setUpdateGeoJson: gJson => dispatch(setUpdateGeoJson(gJson)),
  toggleNewLineAndPoints: showLine =>
    dispatch(toggleNewLineAndPoints(showLine)),
  closeConfirmModal: () => dispatch(closeDeleteConfirm()),
  deleteRoute: (id, eircode) => dispatch(deleteRoute(id, eircode)),
  clearNewRoute: () => dispatch(clearNewRoute()),
  clearUpdateRoute: () => dispatch(clearUpdateRoute()),
  setTab: tab => dispatch(setTab(tab)),
  setEircode: eircode => dispatch(setRouteEircode(eircode)),
  setName: name => dispatch(setRouteName(name)),
  setRelAlt: checked => dispatch(setRouteRelAlt(checked)),
  setDisableSlopedAscent,
  setHighway: highwayKey => dispatch(setHighway(highwayKey)),
  setType: type => dispatch(setRouteType(type))
});

export default connect(mapStateToProps, mapDispatchToProps)(NewRoute);
